/**
 * Raport zarządu — teksty wspólne (`mbr.common.*`) i komunikaty uzgodnień z katalogu MBR_CHECKS (`mbr.checks.*`).
 * Plik FUNDAMENTU. Teksty sekcji: `mbr-<sekcja>.ts`. `en` typowane względem `pl` — brak klucza = błąd kompilacji.
 */
import { mbrKitEn, mbrKitPl } from './mbr-common-kit';
import { mbrDefsEn, mbrDefsPl } from './mbr-defs';

type Shape<T> = { [K in keyof T]: T[K] extends string ? string : Shape<T[K]> };

export const mbrCommonPl = {
  nav: 'Raport zarządu',
  title: 'Raport zarządu',
  subtitle:
    'Zamknięcie miesiąca w jednym miejscu: wskaźniki liczone z danych, porównania, kontrola spójności przed wysyłką do zarządu.',
  period: 'Okres',
  compareLabel: 'Porównanie',
  compare: { MOM: 'm/m', YOY: 'r/r', YTD: 'narastająco', AVG3M: 'śr. 3 mies.' },
  compareLong: {
    MOM: 'vs poprzedni miesiąc',
    YOY: 'vs ten sam miesiąc rok temu',
    YTD: 'od stycznia vs ten sam okres rok temu',
    AVG3M: 'vs średnia 3 poprzednich miesięcy',
  },
  tabs: {
    report: 'Pulpit',
    sales: 'Sprzedaż i rentowność',
    customers: 'Klienci',
    logistics: 'Logistyka i flota',
    operations: 'Magazyn i jakość',
    finance: 'Koszty i należności',
    close: 'Zamknięcie miesiąca',
    settings: 'Ustawienia raportu',
  },
  status: {
    OK: 'Dane kompletne',
    ZERO: 'Zero',
    MISSING: 'Brak danych',
    PARTIAL: 'Dane częściowe',
    RESTRICTED: 'Brak uprawnień',
  },
  missing: 'brak danych',
  missingHint: 'Brak danych to nie zero — uzupełnij je w zamknięciu miesiąca albo w źródle.',
  restricted: 'ukryte',
  frozen: 'Miesiąc zamknięty — dane zamrożone',
  live: 'Na żywo',
  closedChip: 'zamknięty',
  draftChip: 'w przygotowaniu',
  deliveryDays: '{days} dni dostaw',
  deliveryDaysMissing: 'dni dostaw: brak',
  base: 'bazowo {value}',
  trend: 'Trend 12 mies.',
  perDeliveryDay: 'na dzień dostaw',
  loadError: 'Nie udało się wczytać sekcji raportu.',
  stub: 'Sekcja w budowie — dane pojawią się po wdrożeniu etapu.',
  table: {
    metric: 'Wskaźnik',
    current: 'Bieżący',
    base: 'Bazowy',
    delta: 'Zmiana',
    deltaPct: 'Zmiana %',
    empty: 'Brak wierszy.',
    total: 'Razem',
  },
  checksTitle: 'Uzgodnienia danych',
  checksNone: 'Brak rozbieżności.',
  severity: { OK: 'OK', INFO: 'Informacja', WARN: 'Do sprawdzenia', FAIL: 'Rozbieżność' },
  tone: { GOOD: 'dobrze dla firmy', BAD: 'źle dla firmy', NEUTRAL: 'bez istotnej zmiany' },
  unknownCheck: 'Kontrola {code}',
  ...mbrKitPl,
  def: mbrDefsPl,
};

export const mbrCommonEn: Shape<typeof mbrCommonPl> = {
  nav: 'Management report',
  title: 'Management report',
  subtitle:
    'The month close in one place: indicators computed from data, comparisons, consistency checks before it goes to the board.',
  period: 'Period',
  compareLabel: 'Comparison',
  compare: { MOM: 'MoM', YOY: 'YoY', YTD: 'YTD', AVG3M: '3-mo avg' },
  compareLong: {
    MOM: 'vs previous month',
    YOY: 'vs same month last year',
    YTD: 'since January vs same span last year',
    AVG3M: 'vs average of the previous 3 months',
  },
  tabs: {
    report: 'Dashboard',
    sales: 'Sales & profitability',
    customers: 'Customers',
    logistics: 'Logistics & fleet',
    operations: 'Warehouse & quality',
    finance: 'Costs & receivables',
    close: 'Month close',
    settings: 'Report settings',
  },
  status: {
    OK: 'Complete',
    ZERO: 'Zero',
    MISSING: 'No data',
    PARTIAL: 'Partial data',
    RESTRICTED: 'No access',
  },
  missing: 'no data',
  missingHint: 'Missing data is not zero — fill it in the month close or at the source.',
  restricted: 'hidden',
  frozen: 'Month closed — data frozen',
  live: 'Live',
  closedChip: 'closed',
  draftChip: 'in preparation',
  deliveryDays: '{days} delivery days',
  deliveryDaysMissing: 'delivery days: none',
  base: 'base {value}',
  trend: '12-month trend',
  perDeliveryDay: 'per delivery day',
  loadError: 'Could not load the report section.',
  stub: 'Section under construction — data appears once the stage ships.',
  table: {
    metric: 'Indicator',
    current: 'Current',
    base: 'Base',
    delta: 'Change',
    deltaPct: 'Change %',
    empty: 'No rows.',
    total: 'Total',
  },
  checksTitle: 'Data reconciliation',
  checksNone: 'No discrepancies.',
  severity: { OK: 'OK', INFO: 'Info', WARN: 'To check', FAIL: 'Discrepancy' },
  tone: { GOOD: 'good for the company', BAD: 'bad for the company', NEUTRAL: 'no material change' },
  unknownCheck: 'Check {code}',
  ...mbrKitEn,
  def: mbrDefsEn,
};

/** Komunikaty kontroli z katalogu MBR_CHECKS (packages/shared/src/mbr/checks.ts). Zmienne: {expected} {actual} {diff} + params. */
export const mbrChecksPl = {
  BRANCH_SHARE_OF_TOTAL: 'Udział oddziału liczony do całej sprzedaży (nie do Gdańska).',
  BRANCH_SHARE_OF_TOTAL_WARN:
    'Udział oddziału {branch} w źródle danych ({actualPct}) nie jest liczony do całej sprzedaży ({expectedPct}) — raport pokazuje udział do całości.',
  DELTA_FROM_BASE: 'Zmiana procentowa liczona od okresu bazowego.',
  ROUTES_SUM_EQUALS_SALES: 'Suma tras ({actual}) różni się od sprzedaży po korektach ({expected}) o {diff}.',
  RECEIVABLE_BUCKETS_SUM:
    'Przedziały wiekowania ({actual}) nie sumują się do przeterminowanych ({expected}).',
  RECEIVABLE_WEEK_AGO_COPY:
    '„7 dni wstecz” identyczne z bieżącym co do grosza — prawdopodobnie skopiowana kolumna.',
  SECTION_UNCHANGED:
    'Dane identyczne jak w poprzednim miesiącu co do grosza — nieodświeżone albo brak zmian.',
  MARGIN_UNIT: 'Marża zapisana jako ułamek obok wartości w procentach — jedna jednostka w całym raporcie.',
  DATA_MISSING_VS_ZERO: 'Wartość 0 tam, gdzie prawdopodobnie brak danych.',
  PERIOD_YEAR_MISMATCH: 'Rok w danych nie zgadza się z okresem raportu.',
  PAYROLL_COST_ORDER: 'Koszt pracodawcy, brutto i netto w złej relacji (oczekiwane: koszt ≥ brutto ≥ netto).',
  DELIVERY_DAYS_FROM_CALENDAR: 'Dni dostaw z kalendarza tras różnią się od wpisanych ręcznie.',
  COLUMN_ORDER: 'Kolumny okresów w innej kolejności niż w reszcie raportu.',
};

export const mbrChecksEn: Shape<typeof mbrChecksPl> = {
  BRANCH_SHARE_OF_TOTAL: 'Branch share computed against total sales (not against Gdańsk).',
  BRANCH_SHARE_OF_TOTAL_WARN:
    'Share of branch {branch} in the data source ({actualPct}) is not computed against total sales ({expectedPct}) — the report shows the share of the total.',
  DELTA_FROM_BASE: 'Percent change computed from the base period.',
  ROUTES_SUM_EQUALS_SALES:
    'Sum of routes ({actual}) differs from net sales after corrections ({expected}) by {diff}.',
  RECEIVABLE_BUCKETS_SUM: 'Ageing buckets ({actual}) do not add up to overdue ({expected}).',
  RECEIVABLE_WEEK_AGO_COPY:
    '“7 days back” identical to the current value to the grosz — probably a copied column.',
  SECTION_UNCHANGED: 'Data identical to the previous month to the grosz — not refreshed or no change.',
  MARGIN_UNIT: 'Margin stored as a fraction next to percent values — one unit across the report.',
  DATA_MISSING_VS_ZERO: 'Zero where data is probably missing.',
  PERIOD_YEAR_MISMATCH: 'Year in the data does not match the report period.',
  PAYROLL_COST_ORDER: 'Employer cost, gross and net in the wrong order (expected cost ≥ gross ≥ net).',
  DELIVERY_DAYS_FROM_CALENDAR: 'Delivery days from the route calendar differ from the ones typed in.',
  COLUMN_ORDER: 'Period columns in a different order than the rest of the report.',
};
